import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { PermissionPickerItem } from '../utils/permissionLabels';
import './PermissionMultiSelect.css';

export interface PermissionMultiSelectProps {
  value?: string[];
  onChange?: (codes: string[]) => void;
  options: PermissionPickerItem[];
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

function ChevronDownIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <polyline points="2,4 7,10 12,4" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <circle cx="6.5" cy="6.5" r="4.5" />
      <path d="M10.5 10.5L14 14" />
    </svg>
  );
}

const PermissionMultiSelect: React.FC<PermissionMultiSelectProps> = ({
  value,
  onChange,
  options,
  loading = false,
  disabled = false,
  placeholder = 'Select permissions for this user',
}) => {
  const selected = useMemo(() => new Set(value ?? []), [value]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [portalCoords, setPortalCoords] = useState<{ top: number; left: number; width: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const query = search.trim().toLowerCase();

  const byCode = useMemo(() => {
    const m = new Map<string, PermissionPickerItem>();
    options.forEach((o) => m.set(o.code, o));
    return m;
  }, [options]);

  const groupedForList = useMemo(() => {
    const groups: Record<string, PermissionPickerItem[]> = {};
    for (const p of options) {
      if (
        !query ||
        p.name.toLowerCase().includes(query) ||
        p.code.toLowerCase().includes(query) ||
        p.group.toLowerCase().includes(query)
      ) {
        if (!groups[p.group]) groups[p.group] = [];
        groups[p.group].push(p);
      }
    }
    const keys = Object.keys(groups).sort((a, b) => {
      const rank = (k: string) => (k === 'Shortcuts' ? 0 : 1);
      const d = rank(a) - rank(b);
      if (d !== 0) return d;
      return a.localeCompare(b);
    });
    return keys.map((k) => ({ group: k, items: groups[k] }));
  }, [options, query]);

  const close = useCallback(() => {
    setOpen(false);
    setSearch('');
    setPortalCoords(null);
  }, []);

  const measureAndPlace = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPortalCoords({ top: r.bottom + 6, left: r.left, width: r.width });
  }, []);

  const openDropdown = useCallback(() => {
    if (disabled || loading) return;
    setOpen(true);
  }, [disabled, loading]);

  useLayoutEffect(() => {
    if (!open) {
      setPortalCoords(null);
      return;
    }
    measureAndPlace();
  }, [open, measureAndPlace, options.length]);

  useEffect(() => {
    if (!open) return;
    const id = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScrollResize = () => measureAndPlace();
    window.addEventListener('resize', onScrollResize);
    window.addEventListener('scroll', onScrollResize, true);
    return () => {
      window.removeEventListener('resize', onScrollResize);
      window.removeEventListener('scroll', onScrollResize, true);
    };
  }, [open, measureAndPlace]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (dropdownRef.current?.contains(t)) return;
      close();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  const hasAllToken = selected.has('all') || selected.has('*');

  const toggle = useCallback(
    (code: string) => {
      const isAllRow = code === 'all' || code === '*';
      if (isAllRow) {
        if (hasAllToken) {
          onChange?.([]);
        } else {
          onChange?.(['all']);
        }
        return;
      }
      const next = new Set(selected);
      next.delete('all');
      next.delete('*');
      if (next.has(code)) next.delete(code);
      else next.add(code);
      onChange?.(Array.from(next));
    },
    [selected, onChange, hasAllToken]
  );

  const clearAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange?.([]);
    },
    [onChange]
  );

  const selectedOrdered = useMemo(() => {
    const list: PermissionPickerItem[] = [];
    for (const code of value ?? []) {
      const meta =
        byCode.get(code) ??
        (code === '*'
          ? { code: 'all', group: 'Shortcuts', name: 'All permissions' }
          : undefined);
      if (meta) list.push(meta);
    }
    return list;
  }, [value, byCode]);

  const countLabel =
    hasAllToken && selected.size <= 1
      ? 'All permissions (within company plan)'
      : selected.size === 0
        ? 'None selected'
        : `${selected.size} permission${selected.size !== 1 ? 's' : ''} selected`;

  const dropdownContent = (
    <>
      <div className="pms-search-row">
        <span className="pms-search-icon">
          <SearchIcon />
        </span>
        <input
          ref={searchInputRef}
          className="pms-search"
          id="pms-search-input"
          placeholder="Search permissions…"
          autoComplete="off"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
        <button
          type="button"
          className={`pms-clear-search${query ? ' visible' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            setSearch('');
            searchInputRef.current?.focus();
          }}
        >
          ×
        </button>
      </div>
      <div className="pms-list" id="pms-list" onWheel={(e) => e.stopPropagation()}>
        {groupedForList.length === 0 ? (
          <div className="pms-no-results">{query ? `No permissions match "${search.trim()}"` : 'No permissions'}</div>
        ) : (
          groupedForList.map(({ group, items }) => (
            <React.Fragment key={group}>
              <div className="pms-group-label">{group}</div>
              {items.map((p) => {
                const isSel =
                  p.code === 'all'
                    ? selected.has('all') || selected.has('*')
                    : selected.has(p.code);
                return (
                  <div
                    key={p.code}
                    role="option"
                    aria-selected={isSel}
                    className={`pms-item${isSel ? ' selected' : ''}`}
                    data-code={p.code}
                    onClick={() => toggle(p.code)}
                  >
                    <div className="pms-checkbox">
                      <svg className="pms-checkmark" viewBox="0 0 10 10" fill="none" stroke="#fff" strokeWidth="1.8">
                        <polyline points="1.5,5 4,7.5 8.5,2" />
                      </svg>
                    </div>
                    <span className="pms-item-name">{p.name}</span>
                    <span className="pms-item-code">{p.code}</span>
                  </div>
                );
              })}
            </React.Fragment>
          ))
        )}
      </div>
      <div className="pms-footer">
        <span className="pms-count" id="pms-count">
          {countLabel}
        </span>
        <button type="button" className="pms-done" id="pms-done" onClick={close}>
          Done
        </button>
      </div>
    </>
  );

  const onTriggerClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.pms-tag-remove') || (e.target as HTMLElement).closest('.pms-clear-all')) {
      return;
    }
    if (open) close();
    else openDropdown();
  };

  return (
    <div className="pms-root" id="pms" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`pms-trigger${open ? ' open' : ''}`}
        id="pms-trigger"
        onClick={onTriggerClick}
        disabled={disabled || loading}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <div className="pms-tags" id="pms-tags">
          {selectedOrdered.length === 0 ? (
            <span className="pms-placeholder">{placeholder}</span>
          ) : (
            selectedOrdered.map((p) => (
              <span key={p.code} className="pms-tag">
                {p.name}
                <button
                  type="button"
                  className="pms-tag-remove"
                  data-code={p.code}
                  title="Remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(p.code);
                  }}
                >
                  ×
                </button>
              </span>
            ))
          )}
        </div>
        <div className="pms-actions">
          {selectedOrdered.length > 0 && !loading && (
            <button type="button" className="pms-clear-all" title="Clear all" onClick={clearAll}>
              ×
            </button>
          )}
          <span className="pms-chevron" aria-hidden>
            <ChevronDownIcon />
          </span>
        </div>
      </button>

      {loading && <div className="pms-loading">Loading permissions…</div>}

      {open &&
        portalCoords &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={dropdownRef}
            className="pms-dropdown pms-dropdown--portal open"
            id="pms-dropdown"
            role="listbox"
            aria-multiselectable
            style={{
              position: 'fixed',
              top: portalCoords.top,
              left: portalCoords.left,
              width: portalCoords.width,
              zIndex: 2000,
            }}
          >
            {dropdownContent}
          </div>,
          document.body
        )}
    </div>
  );
};

export default PermissionMultiSelect;
